import type { DataTableCell } from '@ui/chart/DataTable';
import type {
	BiPropertyGroup, BiScope, EvolutionSeries, RevenueScope, SegmentationReport, SegmentationRow, SegmentEvolution, SegmentSituation, VatBasis,
} from '@app/model/bi/Bi';
import { occupiedOf, propertyCount, revenueOf, shortName } from '../Bi.utilities';

// The pure functions of the BI Property comparison (no React): the items of the address, and the values of the table and
// the chart. A Property comparison shows one BI report for 2 to 6 items side by side, with the same period and switches;
// an item is one property, one Property group or All properties (grill D1, D2). Each item shows the figures of its own
// report: the page asks the current query once for each item (T1) and never adds items up (T9: two items can hold the
// same property).

export const MAX_ITEMS = 6;

// One item of a comparison. companyHotelId and propertyGroupId are the scope arguments of the current BI queries:
// a property (its id, null), a Property group (null, its id), All properties (null, null).
export type CompareItem = {
	id: string,
	companyHotelId: number | null,
	propertyGroupId: number | null,
	label: string,
	// The second line: the city of a property, the count of a group or of All properties.
	detail: string,
	// false: a property with no Reservations integration (it cannot be compared, D14).
	hasReservations: boolean,
};

const ALL = 'all';
const PROPERTY = 'h:';
const GROUP = 'g:';

// Every item that the user can choose, in the order of the list: All properties, the Property groups (alphabetical), then
// the properties (in the order of the account).
export const compareChoices = (scope: BiScope, groups: BiPropertyGroup[]): CompareItem[] => [
	{ id: ALL, companyHotelId: null, propertyGroupId: null, label: 'All properties', detail: propertyCount(scope.properties.length), hasReservations: true },
	...groups.map(g => ({ id: `${GROUP}${g.id}`, companyHotelId: null, propertyGroupId: g.id, label: g.title, detail: propertyCount(g.properties.length), hasReservations: true })),
	...scope.properties.map(p => ({
		id: `${PROPERTY}${p.companyHotelId}`, companyHotelId: p.companyHotelId, propertyGroupId: null,
		label: shortName(p, scope.companyName), detail: p.area, hasReservations: p.reservationsSource !== null,
	})),
];

// The item of the scope of a report: the property, the Property group or All properties (the start of a comparison, D3).
export const itemIdOfScope = (companyHotelId: number | null, propertyGroupId: number | null): string =>
	companyHotelId !== null ? `${PROPERTY}${companyHotelId}` : (propertyGroupId !== null ? `${GROUP}${propertyGroupId}` : ALL);

// The items of the address (?items=h:204,g:133,all), in their order: an unknown or unreadable item, a property with no
// reservations, a second copy of an item and the items after the 6th drop out (T10, T12); `dropped` counts them. With no
// items in the address, the comparison starts with the scope of the report.
export const itemsOfAddress = (param: string | null, choices: CompareItem[], start: string): { items: CompareItem[], dropped: number } => {
	const ids = param === null ? [start] : param.split(',').map(id => id.trim()).filter(id => id !== '');
	const items: CompareItem[] = [];
	let dropped = 0;
	ids.forEach(id => {
		const item = choices.find(c => c.id === id);
		if (item === undefined || !item.hasReservations || items.some(i => i.id === id) || items.length >= MAX_ITEMS) dropped += 1;
		else items.push(item);
	});
	return { items, dropped };
};

export const itemsParam = (items: { id: string }[]): string => items.map(i => i.id).join(',');

// The address of the report from a comparison: the same query without the items (D13).
export const reportSearch = (search: string): string => {
	const params = new URLSearchParams(search);
	params.delete('items');
	const text = params.toString();
	return text === '' ? '' : `?${text}`;
};

// --- the table (D10) ------------------------------------------------------------------------------------------------------
export type CompareMeasure = 'rn' | 'rev' | 'adr' | 'pu7';

const situation = (row: SegmentationRow | undefined, days: 0 | 7): SegmentSituation | undefined => row?.situations.find(s => s.daysBefore === days);

// The value of a row (or of the total, row = report.total) of one item. Share: the part of the total of that item, for
// Room nights and Revenue only (D5). Revenue and ADR follow the revenue switches; a revenue with no value gives null ("–").
export const compareValue = (report: SegmentationReport, row: SegmentationRow | undefined, measure: CompareMeasure, share: boolean,
	scope: RevenueScope, vat: VatBasis): number | null => {
	if (row === undefined) return null;
	const today = situation(row, 0);
	const total = situation(report.total, 0);
	if (today === undefined || total === undefined) return null;
	const revenue = revenueOf(today, scope, vat);
	if (measure === 'rn') return share ? (total.roomNights === 0 ? null : today.roomNights / total.roomNights) : today.roomNights;
	if (measure === 'rev') {
		if (revenue === null) return null;
		if (!share) return Math.round(revenue);
		const all = revenueOf(total, scope, vat);
		return all === null || all === 0 ? null : revenue / all;
	}
	// ROO2-315 part c1: ADR divides by the occupied rooms of the report rules, as the Segmentation table.
	if (measure === 'adr') return occupiedOf(today) === 0 || revenue === null ? null : Math.round(revenue / occupiedOf(today));
	const then = situation(row, 7);
	return then === undefined ? null : today.roomNights - then.roomNights;
};

// The rows of the table: the Segment Groups in the order of the company, then "Not grouped" and "No segment"; in the PMS
// segments view, the segments by their room nights today over all items, then "No segment". A row that no item has does
// not show; a row that one item lacks shows "–" in its column (D11).
export const compareRows = (reports: (SegmentationReport | undefined)[], groupOrder: string[]): { key: string, title: string, muted: boolean }[] => {
	const rows = new Map<string, { title: string, kind: SegmentationRow['kind'], nights: number }>();
	reports.forEach(r => r?.rows.forEach(row => {
		const known = rows.get(row.key);
		const nights = situation(row, 0)?.roomNights ?? 0;
		rows.set(row.key, { title: row.title, kind: row.kind, nights: (known?.nights ?? 0) + nights });
	}));
	const rank = (key: string, kind: SegmentationRow['kind']) => kind === 'NO_SEGMENT' ? 3 : (kind === 'NOT_GROUPED' ? 2 : (kind === 'GROUP' ? 0 : 1));
	return [...rows.entries()]
		.sort(([ka, a], [kb, b]) => {
			const r = rank(ka, a.kind) - rank(kb, b.kind);
			if (r !== 0) return r;
			if (a.kind === 'GROUP') return groupOrder.indexOf(ka) - groupOrder.indexOf(kb);
			return b.nights !== a.nights ? b.nights - a.nights : a.title.localeCompare(b.title);
		})
		.map(([key, r]) => ({ key, title: r.title, muted: r.kind === 'NOT_GROUPED' || r.kind === 'NO_SEGMENT' }));
};

export const rowOf = (report: SegmentationReport | undefined, key: string): SegmentationRow | undefined => report?.rows.find(r => r.key === key);

// The cell of an item that loads ("…") or failed ("–"), else its value.
export const cellOf = (state: 'loading' | 'error' | 'ready', value: number | null): DataTableCell => state === 'loading' ? '…' : (state === 'error' ? '–' : value);

// --- the chart (D9) -------------------------------------------------------------------------------------------------------
export type CompareMetric = 'roomNights' | 'revenue' | 'adr' | 'mix';

// The stay dates of the evolution query, as the Segment evolution card sends them (the same cache key): the first day of the
// first month and the last day of the last month, YYYY-MM-DD.
export const firstDayOf = (yearMonth: string): string => `${yearMonth}-01`;
export const lastDayOf = (yearMonth: string): string => {
	const days = new Date(Date.UTC(Number(yearMonth.slice(0, 4)), Number(yearMonth.slice(5, 7)), 0)).getUTCDate();
	return `${yearMonth}-${String(days).padStart(2, '0')}`;
};

const revenueSeries = (s: EvolutionSeries, scope: RevenueScope, vat: VatBasis): (number | null)[] => {
	if (scope === 'TOTAL') return vat === 'NET' ? s.totalRevenueNet : s.totalRevenue;
	return vat === 'NET' ? s.roomRevenueNet : s.roomRevenue;
};
const sum = (values: (number | null)[]): number | null => values.some(v => v === null) ? null : values.reduce<number>((t, v) => t + (v ?? 0), 0);

// One line of the chart for one item: the values of the row `key` (null = the Total of the item) in each period. A period
// with no imported stay of the item has no point (the "No data" rule of the report). Mix %: the part of the room nights
// of the item in that period (a row only).
export const compareLine = (evolution: SegmentEvolution, key: string | null, metric: CompareMetric, scope: RevenueScope, vat: VatBasis): (number | null)[] => {
	const series = key === null ? evolution.series : evolution.series.filter(s => s.key === key);
	return evolution.periods.map((period, i) => {
		if (!period.hasData) return null;
		const nights = series.reduce((t, s) => t + s.roomNights[i], 0);
		const occupied = series.reduce((t, s) => t + s.occupiedRooms[i], 0);
		const revenue = sum(series.map(s => revenueSeries(s, scope, vat)[i]));
		if (metric === 'roomNights') return nights;
		if (metric === 'revenue') return revenue === null ? null : Math.round(revenue);
		if (metric === 'adr') return occupied === 0 || revenue === null ? null : Math.round(revenue / occupied);
		const all = evolution.series.reduce((t, s) => t + s.roomNights[i], 0);
		return key === null || all === 0 ? null : nights / all;
	});
};

// --- the load limit (S2) --------------------------------------------------------------------------------------------------
// A runner that lets `max` calls run at the same time; the other calls wait, and the next one starts when one ends (also
// when it fails).
export const limiter = (max: number) => {
	let running = 0;
	const waiting: (() => void)[] = [];
	return async <T>(call: () => Promise<T>): Promise<T> => {
		if (running >= max) await new Promise<void>(resolve => waiting.push(resolve));
		running += 1;
		try {
			return await call();
		} finally {
			running -= 1;
			waiting.shift()?.();
		}
	};
};
